/* global BigInt */

function safeId(value) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}

function rows(response) {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.data) ? response.data : [];
}

export function compatibleTreasuryPeriods(response, salesDateFrom, salesDateTo) {
  if (!salesDateFrom || !salesDateTo || salesDateFrom > salesDateTo) return [];
  return rows(response)
    .filter((period) =>
      period.status === "DRAFT" &&
      period.dateFrom <= salesDateFrom &&
      period.dateTo >= salesDateTo &&
      safeId(period.id) !== null
    )
    .map((period) => ({
      id: safeId(period.id),
      name: String(period.generatedName || ""),
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      status: period.status,
    }));
}

export function incomeTreasuryCategories(response) {
  return rows(response)
    .filter((category) =>
      category.isActive === true &&
      ["INCOME", "BOTH"].includes(category.movementTypeScope) &&
      safeId(category.id) !== null
    )
    .map((category) => ({ id: safeId(category.id), name: String(category.name || "") }));
}

export function preferredSalesTreasuryCategoryId(categories) {
  const importedSales = categories.filter((category) =>
    category.name.trim().toLocaleLowerCase("es-AR") === "ventas importadas"
  );
  return importedSales.length === 1 ? importedSales[0].id : null;
}

export function availableTreasuryAccounts(accountsResponse, balancesResponse) {
  const availableIds = new Set(rows(balancesResponse).map((balance) => safeId(balance.accountId)));
  return rows(accountsResponse)
    .filter((account) => account.isActive === true && availableIds.has(safeId(account.id)))
    .map((account) => ({ id: safeId(account.id), name: String(account.name || "") }))
    .filter((account) => account.id !== null);
}

export function parseCanonicalTreasuryAmountCents(value) {
  const normalized = String(value ?? "").trim();
  if (!/^\d{1,13}(?:\.\d{1,10})?$/.test(normalized)) return null;
  const [whole, fraction = ""] = normalized.split(".");
  if (fraction.slice(2).replace(/0/g, "")) return null;
  return BigInt(whole) * 100n + BigInt((fraction + "00").slice(0, 2));
}

export function parseTreasuryAmountCents(value) {
  const normalized = String(value ?? "").trim();
  if (/^\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?$/.test(normalized)) {
    return parseCanonicalTreasuryAmountCents(normalized.replace(/\./g, "").replace(",", "."));
  }
  if (/^\d{1,13}(?:,\d{1,2})?$/.test(normalized)) {
    return parseCanonicalTreasuryAmountCents(normalized.replace(",", "."));
  }
  // Compatibilidad con valores canónicos guardados antes de formatear el campo.
  return /^\d{1,13}\.\d{1,2}$/.test(normalized) ? parseCanonicalTreasuryAmountCents(normalized) : null;
}

export function formatTreasuryAmountInput(value) {
  const cleaned = String(value ?? "").replace(/[^\d,]/g, "");
  const comma = cleaned.indexOf(",");
  const whole = (comma < 0 ? cleaned : cleaned.slice(0, comma)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  if (comma < 0) return whole;
  return `${whole},${cleaned.slice(comma + 1).replace(/,/g, "").slice(0, 2)}`;
}

export function formatTreasuryAmountFromCanonical(value) {
  const cents = parseCanonicalTreasuryAmountCents(value);
  return cents === null ? "" : formatTreasuryAmountInput(treasuryAmountFromCents(cents).replace(".", ","));
}

export function treasuryAmountFromCents(cents) {
  const sign = cents < 0n ? "-" : "";
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

export function treasuryDistribution(allocations, totalSales) {
  const totalCents = parseCanonicalTreasuryAmountCents(totalSales);
  const amountCents = allocations.map((allocation) => parseTreasuryAmountCents(allocation.amount));
  const distributedCents = amountCents.reduce((sum, amount) => sum + (amount ?? 0n), 0n);
  const accountIds = allocations.map((allocation) => Number(allocation.accountId));
  const complete = totalCents !== null && totalCents > 0n && allocations.length > 0 &&
    allocations.every((allocation, index) =>
      Number.isSafeInteger(accountIds[index]) && accountIds[index] > 0 && amountCents[index] !== null && amountCents[index] > 0n
    ) && new Set(accountIds).size === accountIds.length;
  const difference = totalCents === null ? 0n : totalCents - distributedCents;
  return {
    complete,
    exact: complete && difference === 0n,
    distributed: treasuryAmountFromCents(distributedCents),
    pending: treasuryAmountFromCents(difference > 0n ? difference : 0n),
    excess: treasuryAmountFromCents(difference < 0n ? -difference : 0n),
  };
}
